import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

interface GlobalSearchState {
  open: boolean;
  setOpen: (open: boolean) => void;
  openSearch: () => void;
}

const NOOP_STATE: GlobalSearchState = {
  open: false,
  setOpen: () => undefined,
  openSearch: () => undefined,
};

const GlobalSearchContext = createContext<GlobalSearchState>(NOOP_STATE);

/**
 * Owns the global search dialog state and the / and ⌘K / Ctrl+K shortcuts.
 *
 * Kept free of data hooks on purpose: the dialog content (queries, account,
 * authorization) only mounts while the dialog is open.
 */
export function GlobalSearchProvider({
  children,
  enabled = true,
}: {
  children: ReactNode;
  enabled?: boolean;
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.defaultPrevented || event.isComposing || event.repeat) return;
      if (event.key === "/" && !event.metaKey && !event.ctrlKey && !event.altKey) {
        const target = event.target;
        if (
          target instanceof HTMLElement &&
          (target.isContentEditable ||
            target.closest(
              "input, textarea, select, [role='textbox'], [role='combobox'], [role='dialog'], [role='alertdialog']",
            ))
        )
          return;
        event.preventDefault();
        setOpen(true);
        return;
      }
      if (event.key.toLowerCase() !== "k" || !(event.metaKey || event.ctrlKey)) return;
      if (event.altKey || event.shiftKey || event.repeat) return;
      event.preventDefault();
      setOpen((current) => !current);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled]);

  const value = useMemo<GlobalSearchState>(
    () => (enabled ? { open, setOpen, openSearch: () => setOpen(true) } : NOOP_STATE),
    [open, enabled],
  );

  return <GlobalSearchContext value={value}>{children}</GlobalSearchContext>;
}

export function useGlobalSearch() {
  return useContext(GlobalSearchContext);
}

export function getSearchShortcutLabel() {
  return "/";
}
